import {INavigate} from '../models';
import {NavigationPageEnum, NavigationTabEnum} from '../models/navigations.enums';

export interface IParsedRouterNavigation {
  feature: NavigationPageEnum;
  navigation: INavigate;
  /**
   * True when the URL has no explicit `?tab=` query param.
   * When syncing the same parent feature page, the current shell tab is preserved.
   */
  preserveExistingTab: boolean;
}

/**
 * Resolves shell navigation state from a router URL (including Back/Forward).
 * Prefers the longest matching {@link NavigationPageEnum} path so nested routes
 * such as `barcode-scanner/home` win over a shorter prefix.
 */
export function parseNavigationFromRouterUrl(url: string): IParsedRouterNavigation | null {
  if (!url) {
    return null;
  }

  const [pathWithLeadingSlash, queryString = '']: string[] = url.split('?');
  const path: string = pathWithLeadingSlash.replace(/^\//, '').replace(/\/$/, '');
  if (!path || path === 'auth' || path.startsWith('auth/')) {
    return null;
  }

  const pageValues: string[] = Object.values(NavigationPageEnum).sort(
    (pageA: string, pageB: string): number => pageB.length - pageA.length,
  );
  const matchedPage: string | undefined = pageValues.find(
    (page: string): boolean => path === page || path.startsWith(`${page}/`),
  );
  if (!matchedPage) {
    return null;
  }

  if (matchedPage === NavigationPageEnum.Login) {
    return null;
  }

  const tabParam: string | null = new URLSearchParams(queryString).get('tab');
  const tabValues: string[] = Object.values(NavigationTabEnum);
  const hasExplicitTab: boolean = !!tabParam && tabValues.includes(tabParam);
  const currentTab: NavigationTabEnum | null = hasExplicitTab
    ? (tabParam as NavigationTabEnum)
    : null;

  return {
    feature: matchedPage as NavigationPageEnum,
    preserveExistingTab: !hasExplicitTab,
    navigation: {
      currentPage: matchedPage as NavigationPageEnum,
      currentTab,
    },
  };
}

/**
 * Preserves the current shell tab when the URL has no explicit `?tab=` and the
 * matched page is the same feature already selected (parent URL or nested child).
 */
export function resolveSyncedNavigation(
  parsed: IParsedRouterNavigation,
  current: INavigate | null | undefined,
): INavigate {
  if (
    parsed.preserveExistingTab
    && current?.currentPage === parsed.navigation.currentPage
  ) {
    return {
      currentPage: parsed.navigation.currentPage,
      currentTab: current.currentTab ?? null,
    };
  }
  return parsed.navigation;
}

/**
 * True when store navigation already matches the parsed router navigation.
 */
export function isSameNavigationState(
  current: INavigate | null | undefined,
  next: INavigate,
): boolean {
  if (!current) {
    return false;
  }
  return current.currentPage === next.currentPage && (current.currentTab ?? null) === (next.currentTab ?? null);
}
